/**
 * PortraitManager - Handles visual novel style character portraits
 * Creates and manages portrait displays for NPCs in dialogue
 */

export class PortraitManager {
  constructor(scene) {
    this.scene = scene;
    this.currentPortrait = null;
  }

  /**
   * Show a character portrait with visual novel styling
   * @param {Object} npc - NPC data (name, traits, etc.)
   * @param {string} expression - Expression to show (neutral, happy, surprised, etc.)
   */
  showPortrait(npc, expression = 'neutral') {
    // Remove existing portrait
    this.hidePortrait();

    // Create portrait container
    const container = this.scene.add.container(600, 300);
    container.setDepth(100);

    // Portrait background frame
    const frame = this.scene.add.graphics();
    frame.fillStyle(0x2C1810, 0.9);
    frame.fillRoundedRect(-150, -200, 300, 400, 10);
    frame.lineStyle(4, 0xD4AF37);
    frame.strokeRoundedRect(-150, -200, 300, 400, 10);
    container.add(frame);

    // Generate portrait based on NPC data
    const portrait = this.generatePortrait(npc, expression);
    container.add(portrait);

    // Name plate
    const nameplate = this.createNameplate(npc.name);
    nameplate.setPosition(0, 220);
    container.add(nameplate);

    this.currentPortrait = container;
    return container;
  }

  /**
   * Generate a portrait for an NPC - SPRITES ONLY
   */
  generatePortrait(npc, expression) {
    const portraitGroup = this.scene.add.container(0, 0);
    
    const spriteKey = `portrait-${npc.id}-${expression}`;
    
    console.log(`[PortraitManager] Looking for sprite: ${spriteKey}`);
    console.log(`[PortraitManager] Texture exists: ${this.scene.textures.exists(spriteKey)}`);
    
    if (!this.scene.textures.exists(spriteKey)) {
      console.error(`[PortraitManager] ✗ SPRITE NOT FOUND: ${spriteKey}`);
      // Just show error text instead of procedural graphics
      const errorText = this.scene.add.text(0, 0, `IMAGE NOT LOADED\n${spriteKey}`, {
        fontSize: '14px',
        fill: '#FF0000',
        align: 'center',
        backgroundColor: '#000000',
        padding: { x: 10, y: 10 }
      }).setOrigin(0.5);
      portraitGroup.add(errorText);
      return portraitGroup;
    }
    
    console.log(`[PortraitManager] ✓ Using sprite for ${spriteKey}`);
    const sprite = this.scene.add.sprite(0, -20, spriteKey);
    sprite.setScale(0.35);
    portraitGroup.add(sprite);
    
    // Debug indicator
    const debugText = this.scene.add.text(-130, -180, '✓ REAL IMAGE', {
      fontSize: '12px',
      fill: '#00FF00',
      backgroundColor: '#000000',
      padding: { x: 4, y: 2 }
    });
    portraitGroup.add(debugText);
    
    return portraitGroup;
  }
  
  // REMOVED ALL PROCEDURAL GRAPHICS CODE
  generatePortrait_OLD_PROCEDURAL(npc, expression) {
    const portraitGroup = this.scene.add.container(0, 0);
    const g = this.scene.add.graphics();
    portraitGroup.add(g);

    // Background gradient
    g.fillGradientStyle(0x4A5568, 0x4A5568, 0x2D3748, 0x2D3748, 1);
    g.fillRect(-140, -190, 280, 380);

    // Get NPC-specific colors
    const skinTone = this.getSkinTone(npc);
    const hairColor = this.getHairColor(npc);
    const eyeColor = this.getEyeColor(npc);
    const clothing = this.getClothingColor(npc);

    // Head/neck
    g.fillStyle(skinTone);
    g.fillEllipse(0, -60, 80, 100);
    
    // Neck
    g.fillRect(-25, 20, 50, 60);

    // Shoulders (clothing)
    g.fillStyle(clothing);
    g.beginPath();
    g.moveTo(-90, 80);
    g.lineTo(-50, 30);
    g.lineTo(50, 30);
    g.lineTo(90, 80);
    g.lineTo(90, 180);
    g.lineTo(-90, 180);
    g.closePath();
    g.fillPath();

    // Collar detail
    g.lineStyle(3, 0xFFFFFF, 0.3);
    g.beginPath();
    g.moveTo(-30, 40);
    g.lineTo(0, 50);
    g.lineTo(30, 40);
    g.strokePath();

    // Hair
    this.drawHair(g, hairColor, npc);

    // Eyes
    this.drawEyes(g, eyeColor, expression);

    // Mouth
    this.drawMouth(g, expression);

    // Add character-specific features
    this.addCharacterFeatures(g, npc);

    return portraitGroup;
  }

  drawHair(g, color, npc) {
    g.fillStyle(color);
    
    // Different hairstyles based on NPC
    const style = npc.hairStyle || 'medium';
    
    switch(style) {
      case 'short':
        // Short professional cut
        g.fillEllipse(0, -100, 85, 60);
        g.fillRect(-75, -100, 150, 50);
        break;
      
      case 'long':
        // Long hair
        g.fillEllipse(0, -100, 90, 70);
        g.fillRect(-80, -100, 160, 80);
        // Sides
        g.fillEllipse(-60, -20, 30, 100);
        g.fillEllipse(60, -20, 30, 100);
        break;
      
      case 'medium':
      default:
        // Medium length
        g.fillEllipse(0, -100, 90, 65);
        g.fillRect(-80, -100, 160, 60);
        break;
    }
  }

  drawEyes(g, color, expression) {
    // Eye whites
    g.fillStyle(0xFFFFFF);
    g.fillEllipse(-30, -70, 18, 20);
    g.fillEllipse(30, -70, 18, 20);

    // Irises
    g.fillStyle(color);
    const pupilOffsetX = expression === 'surprised' ? 0 : 2;
    g.fillEllipse(-30 + pupilOffsetX, -70, 12, 14);
    g.fillEllipse(30 + pupilOffsetX, -70, 12, 14);

    // Pupils
    g.fillStyle(0x000000);
    g.fillEllipse(-30 + pupilOffsetX, -70, 6, 8);
    g.fillEllipse(30 + pupilOffsetX, -70, 6, 8);

    // Eye highlights
    g.fillStyle(0xFFFFFF);
    g.fillCircle(-33 + pupilOffsetX, -75, 3);
    g.fillCircle(27 + pupilOffsetX, -75, 3);

    // Eyebrows
    g.lineStyle(4, 0x3E2723);
    if (expression === 'surprised') {
      g.beginPath();
      g.arc(-30, -95, 18, 0.3, Math.PI - 0.3, true);
      g.strokePath();
      g.beginPath();
      g.arc(30, -95, 18, 0.3, Math.PI - 0.3, true);
      g.strokePath();
    } else if (expression === 'happy') {
      g.beginPath();
      g.moveTo(-45, -88);
      g.lineTo(-15, -90);
      g.strokePath();
      g.beginPath();
      g.moveTo(15, -90);
      g.lineTo(45, -88);
      g.strokePath();
    } else {
      // neutral
      g.beginPath();
      g.moveTo(-45, -90);
      g.lineTo(-15, -90);
      g.strokePath();
      g.beginPath();
      g.moveTo(15, -90);
      g.lineTo(45, -90);
      g.strokePath();
    }
  }

  drawMouth(g, expression) {
    g.lineStyle(3, 0x8B5A5A);
    
    switch(expression) {
      case 'happy':
        // Big smile
        g.beginPath();
        g.arc(0, -35, 15, 0.2, Math.PI - 0.2, false);
        g.strokePath();
        break;
      
      case 'surprised':
        // O shape
        g.fillStyle(0x3E2723);
        g.fillEllipse(0, -30, 8, 12);
        break;
      
      case 'neutral':
      default:
        // Slight smile
        g.beginPath();
        g.arc(0, -40, 12, 0.3, Math.PI - 0.3, false);
        g.strokePath();
        break;
    }
  }

  addCharacterFeatures(g, npc) {
    // Add glasses if NPC has them
    if (npc.hasGlasses) {
      g.lineStyle(2, 0x2C2C2C);
      g.strokeEllipse(-30, -70, 20, 22);
      g.strokeEllipse(30, -70, 20, 22);
      g.beginPath();
      g.moveTo(-10, -70);
      g.lineTo(10, -70);
      g.strokePath();
    }
  }

  createNameplate(name) {
    const container = this.scene.add.container(0, 0);
    
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x2C1810, 0.95);
    bg.fillRoundedRect(-100, -20, 200, 40, 8);
    bg.lineStyle(2, 0xD4AF37);
    bg.strokeRoundedRect(-100, -20, 200, 40, 8);
    container.add(bg);

    const nameText = this.scene.add.text(0, 0, name, {
      fontSize: '20px',
      fill: '#FFD700',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(nameText);

    return container;
  }

  hidePortrait() {
    if (this.currentPortrait) {
      this.currentPortrait.destroy();
      this.currentPortrait = null;
    }
  }

  // Helper methods for NPC appearance
  getSkinTone(npc) {
    const tones = {
      'Maya Chen': 0xE8BEAC,
      'James Morrison': 0xF5D7C3,
      'Dr. Eliza Okonkwo': 0x8D5524,
      'River': 0xC68642,
      'Tommy Park': 0xFFDBB0
    };
    return tones[npc.name] || 0xE8BEAC;
  }

  getHairColor(npc) {
    const colors = {
      'Maya Chen': 0x2C1810,
      'James Morrison': 0x6B5B3D,
      'Dr. Eliza Okonkwo': 0x1C0F0A,
      'River': 0x3A2820,
      'Tommy Park': 0x2C1810
    };
    return colors[npc.name] || 0x4A3728;
  }

  getEyeColor(npc) {
    const colors = {
      'Maya Chen': 0x3E2723,
      'James Morrison': 0x5D7AA8,
      'Dr. Eliza Okonkwo': 0x3E2723,
      'River': 0x698B69,
      'Tommy Park': 0x3E2723
    };
    return colors[npc.name] || 0x3E2723;
  }

  getClothingColor(npc) {
    const colors = {
      'Maya Chen': 0xE8B86D, // warm yellow/tan
      'James Morrison': 0x4A5568, // business gray
      'Dr. Eliza Okonkwo': 0x2C5F7F, // professional blue
      'River': 0x3E3E3E, // dark mysterious
      'Tommy Park': 0x6B8E23 // olive green hoodie
    };
    return colors[npc.name] || 0x4A5568;
  }
}
